import { styled } from '@mui/material/styles';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';
import SortableTableHead from 'Components/TableBase/SortableTableHead';
import { StyledTable } from 'Components/TableBase/Table';
import { IProduct } from 'FeatureModules/ProductPage/types/product.types';
import PRODUCT_TABLE_HEADER_COLUMNS from 'FeatureModules/ProductPage/utils/headerColumn.utils';
import React, { FC, MouseEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { TSortOrder } from 'types/pagination.types';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { APP_QUERY_PARAMS } from 'utils/queryParam.utils';
import ProductTableBody from './ProductTableBody';

interface TableAction {
  icon: ReactNode;
  tooltip: string;
  onClick: (product: IProduct) => void;
  dataCy?: string;
}

interface IProductTableProps {
  actions: TableAction[];
  products: IProduct[];
  hasNextPage: boolean;
  archived?: boolean;
  'data-testId'?: string;
  onPageEndReached: () => void;
}

const Container = styled('div')(() => ({
  minWidth: 1100,
}));

export const UnarchiveIcon = styled('img')(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const ProductTable: FC<IProductTableProps> = ({
  actions,
  products,
  archived,
  'data-testId': dataCy = '',
  hasNextPage,
  onPageEndReached,
}) => {
  const navigate = useNavigate();

  const [query, setQuery] = useQueryParams({
    [APP_QUERY_PARAMS.order]: withDefault(StringParam, 'DESC'),
    [APP_QUERY_PARAMS.orderBy]: withDefault(StringParam, 'MODIFIED_TIMESTAMP'),
  });
  const order = query[APP_QUERY_PARAMS.order] as TSortOrder | undefined;
  const orderBy = query[APP_QUERY_PARAMS.orderBy];

  const orderTableHeader = order === 'ASC' ? 'asc' : 'desc';

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'ASC';
    setQuery({
      [APP_QUERY_PARAMS.order]: isAsc ? 'DESC' : 'ASC',
    });
    setQuery({
      [APP_QUERY_PARAMS.orderBy]: property,
    });
  };

  const handleSelectRow = (event: MouseEvent, id: string) => {
    event.stopPropagation();
    navigate(`/products/active/${id}`);
  };

  return (
    <Container>
      <InfiniteScrollWrapper hasMore={hasNextPage} next={onPageEndReached} scrollThreshold={0.6}>
        <StyledTable aria-labelledby="tableTitle" aria-label="enhanced table">
          {!!products.length && (
            <SortableTableHead
              onSort={handleRequestSort}
              order={orderTableHeader}
              orderBy={orderBy}
              headerColumns={PRODUCT_TABLE_HEADER_COLUMNS}
            />
          )}

          <ProductTableBody
            data-testid={dataCy}
            actions={actions}
            products={products}
            onSelect={handleSelectRow}
            archived={archived}
          />
        </StyledTable>
      </InfiniteScrollWrapper>
    </Container>
  );
};

export default ProductTable;
